import type {
    LatexNode,
    TextNode,
    SuperscriptNode,
    FractionNode,
    SqrtNode,
    ParenthesesNode,
    ResizableParenthesesNode,
} from '../types';
import { tokenize } from './tokenize';

export function parseLatex(input: string): LatexNode {
    const tokens = tokenize(input);
    let index = 0;

    return parseExpression();

    // Assert that a specific token should come next
    function expect(expected: '{' | '}' | '[' | ']' | '(' | ')') {
        const token = tokens[index];
        const isBrace = token?.type === 'brace' && token.value === expected;
        const isParen = token?.type === 'parentheses' && token.value === expected;

        if (!isBrace && !isParen) {
            throw new Error(`Expected '${expected}', found ${JSON.stringify(token)}`);
        }

        index++;
    }

    // Parse a singular base-level expression in brackets or parentheses
    // Returns one node with no chlidren
    function parseExpression(): LatexNode {
        let node = parsePrimary(); // start with one group

        while (index < tokens.length) {
            const token = tokens[index];

            if (token.type === 'command' && token.value === '^') {
                index++; // consume ^
                const exponent = parsePrimary();
                node = {
                    type: 'superscript',
                    base: node,
                    exponent,
                };
                continue;
            }

            // Stop if we hit a closing brace or unhandled token
            if (token.type === 'brace' && token.value === '}') {
                break;
            }
            if (token.type === 'parentheses' && token.value === ')') {
                break;
            }

            break;
        }

        return node;
    }

    // Parse a group of expressions
    // Returns one node with no children, or a node with a list of children
    function parseGroup(): LatexNode {
        const children: LatexNode[] = [];

        while (index < tokens.length) {
            const token = tokens[index];

            if (token.type === 'brace' && ['}', ']'].includes(token.value)) {
                break; // Don't consume it yet — caller will
            }

            children.push(parseExpression());
        }

        if (children.length === 1) {
            return children[0]; // Return the single node as-is
        }

        // Return a synthetic 'group' node
        return {
            type: 'group',
            children,
        };
    }

    // Parse the next token
    // \frac{1^2}{2} --> frac, {, 1, ^, 2, }, {, 2, }
    // 1 ^ 2 --> 1, ^, 2
    // 2 ^ 2 ^ 2
    function parsePrimary(): LatexNode {
        const token = tokens[index];

        if (!token) throw new Error('Unexpected end of input');

        if (token.type === 'symbol') {
            index++; // consume letter or number "symbol" token
            return { type: 'text', value: token.value } satisfies TextNode;
        }

        if (token.type === 'command') {
            // I don't think this is ever called? Seems to be handled in parseExpression
            if (token.value === '^') {
                index++; // consume ^
                const base = parseGroup();
                const exponent = parseGroup();
                return { type: 'superscript', base, exponent } satisfies SuperscriptNode;
            }

            if (token.value === 'frac') {
                index++; // consume \frac
                expect('{');
                const numerator = parseGroup();
                expect('}');
                expect('{');
                const denominator = parseGroup();
                expect('}');
                return { type: 'fraction', numerator, denominator } satisfies FractionNode;
            }

            if (token.value === 'sqrt') {
                index++; // consume \sqrt

                let indexNode: LatexNode | undefined;
                const token = tokens[index];
                if (token?.type === 'brace' && token.value! === '[') {
                    index++; // consume [
                    indexNode = parseGroup(); // parse the index
                    expect(']');
                }

                expect('{');
                const value = parseGroup();
                expect('}');
                return { type: 'sqrt', value, index: indexNode } satisfies SqrtNode;
            }

            // Resizable parentheses
            if (token.value === 'left') {
                index++; // consume \left

                const leftDelimToken = tokens[index++];
                if (leftDelimToken?.type !== 'parentheses' && leftDelimToken?.type !== 'brace') {
                    throw new Error('Expected delimiter after \\left');
                }

                const left = leftDelimToken.value;

                const content = parseExpression();

                const rightCommand = tokens[index++];
                if (rightCommand?.type !== 'command' || rightCommand.value !== 'right') {
                    throw new Error('Expected \\right to close \\left');
                }

                const rightDelimToken = tokens[index++];
                if (rightDelimToken?.type !== 'parentheses' && rightDelimToken?.type !== 'brace') {
                    throw new Error('Expected delimiter after \\right');
                }

                const right = rightDelimToken.value;

                return {
                    type: 'resizable-parentheses',
                    left,
                    right,
                    content,
                } satisfies ResizableParenthesesNode;
            }

            throw new Error('Unsupported command: \\' + token.value);
        }

        if (token.type === 'brace' && token.value === '{') {
            index++;
            const node = parseGroup(); // Parse until next }
            expect('}');
            return node;
        }

        if (token.type === 'parentheses' && token.value === '(') {
            index++; // consume '('
            const content = parseExpression();
            expect(')');
            return { type: 'parentheses', content } satisfies ParenthesesNode;
        }

        throw new Error('Unexpected token: ' + JSON.stringify(token));
    }
}
